import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Plus, Save, Loader2, AlertCircle, Library } from 'lucide-react'
import { DndContext, closestCenter, type DragEndEvent } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { FieldBlock } from '../components/FormBuilder/FieldBlock'
import { FieldTypePicker } from '../components/FormBuilder/FieldTypePicker'
import { LibraryFieldPicker } from '../components/FormBuilder/LibraryFieldPicker'
import { FieldConfigPanel } from '../components/FormBuilder/FieldConfigPanel'
import { useAdminSectionTemplate } from '../hooks/useAdminSectionTemplate'
import type { ApplicationField } from '@/types'

export function AdminSectionTemplateEditPage() {
  const { id } = useParams<{ id: string }>()
  const [editingField, setEditingField] = useState<ApplicationField | null>(null)
  const [configOpen, setConfigOpen] = useState(false)
  const [addFieldOpen, setAddFieldOpen] = useState(false)
  const [libraryOpen, setLibraryOpen] = useState(false)

  const {
    loading,
    saving,
    error,
    isDirty,
    metadata,
    fields,
    setMetadata,
    addField,
    addFieldFromLibrary,
    updateField,
    deleteField,
    reorderFields,
    save,
  } = useAdminSectionTemplate(id ?? '')

  const sortedFields = [...fields].sort((a, b) => a.order - b.order)

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = sortedFields.findIndex((f) => f.id === active.id)
    const newIndex = sortedFields.findIndex((f) => f.id === over.id)
    const reordered = arrayMove(sortedFields, oldIndex, newIndex)
    reorderFields(reordered.map((f) => f.id))
  }

  function openFieldEdit(field: ApplicationField) {
    setEditingField(field)
    setConfigOpen(true)
  }

  function handleFieldUpdate(patch: Partial<ApplicationField>) {
    if (!editingField) return
    const updated = { ...editingField, ...patch }
    setEditingField(updated)
    updateField(editingField.id, patch)
  }

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button asChild variant="ghost" size="sm">
            <Link to="/admin/section-library">← Back to Section Library</Link>
          </Button>
          <h1 className="text-2xl font-bold">Edit Section Template</h1>
          {isDirty && (
            <Badge variant="secondary" className="text-xs">
              Unsaved changes
            </Badge>
          )}
        </div>
        <Button onClick={() => void save()} disabled={saving || !isDirty}>
          {saving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving…
            </>
          ) : (
            <>
              <Save className="mr-2 h-4 w-4" />
              Save Template
            </>
          )}
        </Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Metadata */}
      <div className="space-y-4 rounded-lg border bg-card p-4 shadow-sm">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-1">
            <Label>Section ID</Label>
            <Input className="font-mono" value={metadata.sectionId} disabled />
            <p className="text-xs text-muted-foreground">Cannot be changed after creation.</p>
          </div>
          <div className="space-y-1">
            <Label>Category</Label>
            <Input
              value={metadata.category}
              onChange={(e) => setMetadata({ category: e.target.value })}
            />
          </div>
        </div>
        <div className="space-y-1">
          <Label>Title</Label>
          <Input
            value={metadata.title}
            onChange={(e) => setMetadata({ title: e.target.value })}
          />
        </div>
        <div className="space-y-1">
          <Label>Description (optional)</Label>
          <Textarea
            rows={2}
            value={metadata.description}
            onChange={(e) => setMetadata({ description: e.target.value })}
          />
        </div>
      </div>

      {/* Fields */}
      <div className="space-y-3 rounded-lg border bg-card p-4 shadow-sm">
        <h2 className="text-sm font-semibold">Fields</h2>
        <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext
            items={sortedFields.map((f) => f.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-2">
              {sortedFields.map((field) => (
                <FieldBlock
                  key={field.id}
                  field={field}
                  onEdit={() => openFieldEdit(field)}
                  onDelete={() => deleteField(field.id)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>

        {sortedFields.length === 0 && (
          <p className="py-4 text-center text-sm text-muted-foreground">
            No fields in this template. Add one below.
          </p>
        )}

        <div className="flex gap-2">
          <Button type="button" variant="outline" size="sm" onClick={() => setAddFieldOpen(true)}>
            <Plus className="mr-1 h-3.5 w-3.5" />
            Add Field
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={() => setLibraryOpen(true)}>
            <Library className="mr-1 h-3.5 w-3.5" />
            From Library
          </Button>
        </div>
      </div>

      <FieldTypePicker open={addFieldOpen} onOpenChange={setAddFieldOpen} onSelect={addField} />
      <LibraryFieldPicker
        open={libraryOpen}
        onOpenChange={setLibraryOpen}
        onSelect={addFieldFromLibrary}
      />
      <FieldConfigPanel
        field={editingField}
        open={configOpen}
        onOpenChange={setConfigOpen}
        onUpdate={handleFieldUpdate}
      />
    </div>
  )
}
